import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useEffect, useMemo } from 'react';

interface VirtualisedBlocksGridProps {
    blocksToRender: React.ReactNode[];
    columns: number;
    hasSublabels: boolean;
    blockHeight?: number;
    limitHeight?: boolean;
}

const getRows = (blocksToRender: React.ReactNode[], columns: number) => {
    const rows: React.ReactNode[][] = [];

    let count = 0;
    while (count < blocksToRender.length) {
        const row = blocksToRender.slice(count, count + columns);
        rows.push(row);
        count += columns;
    }

    return rows;
};

const MARGIN = 7;

export const VirtualisedBlocksGrid: React.FC<VirtualisedBlocksGridProps> = ({
    hasSublabels,
    blocksToRender,
    columns,
    blockHeight
}) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);

    const rows = useMemo(() => getRows(blocksToRender, columns), [blocksToRender, columns]);

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        overscan: 24,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => blockHeight || (hasSublabels ? 45 : 30),
        gap: MARGIN
    });

    useEffect(() => {
        rowVirtualizer.measure();
    }, [blockHeight, hasSublabels, rowVirtualizer]);

    return (
        <div ref={scrollRef} className='h-full overflow-auto text-primaryButtonText tile-scroll-overflow'>
            <div className='relative w-full' style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                {rowVirtualizer.getVirtualItems().map((row) => (
                    <div
                        key={row.key.toString()}
                        className='absolute w-full grid gap-2'
                        style={{
                            height: `${row.size}px`,
                            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                            transform: `translateY(${row.start}px)`
                        }}
                    >
                        {rows[row.index]}
                    </div>
                ))}
            </div>
        </div>
    );
};
